"use client";

import React from "react";
import { Anchor, CircleCheck, Loader, Ship, Building2, Calendar, User, Phone, UserCheck, FileText } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
const inputClasses = "rounded-[2px] bg-muted/20 border-x-0 border-t-0 border-b-border focus-visible:ring-0 focus-visible:border-b-primary focus-visible:border-b-2 transition-all";
function ProcurementFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-6 rounded-[2px] border border-border bg-card p-10 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex size-16 items-center justify-center rounded-[2px] bg-primary/10">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-serif text-2xl font-bold tracking-tight text-foreground">Request Received</h3>
          <p className="max-w-md text-muted-foreground">{successMessage}</p>
        </div>
        <Button variant="outline" onClick={resetForm} className="mt-4 rounded-[2px] border-secondary text-secondary-foreground hover:bg-secondary/10 shadow-none">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form id="procurement-requirements-form" onSubmit={handleSubmit} className="space-y-12">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <Building2 className="size-5 text-primary" />
          <h4 className="font-serif text-xl font-semibold">Company Details</h4>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField control={control} name="company_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Company Name</FormLabel>
                <FormControl>
                  <Input className={inputClasses} placeholder="e.g., Oceanic Logistics Ltd." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="contact_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Contact Person</FormLabel>
                <FormControl>
                  <Input className={inputClasses} placeholder="John Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" className={inputClasses} placeholder="john.doe@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" className={inputClasses} placeholder="+1 (555) 123-1114-567" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <Ship className="size-5 text-primary" />
          <h4 className="font-serif text-xl font-semibold">Vessel & Schedule</h4>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField control={control} name="vessel_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Vessel Name</FormLabel>
                <FormControl>
                  <Input className={inputClasses} placeholder="e.g., MV Oceanic" {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[11px]">Enter the registered name of the vessel.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="imo_number" rules={{
          pattern: {
            value: /^[0-9]{7}$/,
            message: "Must be a 7-digit number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">IMO Number</FormLabel>
                <FormControl>
                  <Input className={inputClasses} placeholder="e.g., 1234567" {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[11px]">Seven-digit International Maritime Organization number.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="eta" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">ETA (Estimated Time of Arrival)</FormLabel>
                <FormControl>
                  <Input type="date" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="etd" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">ETD (Estimated Time of Departure)</FormLabel>
                <FormControl>
                  <Input type="date" className={inputClasses} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="next_port_of_call" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Next Port of Call</FormLabel>
                <FormControl>
                  <Input className={inputClasses} placeholder="e.g., Port of Rotterdam" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="second_port_of_call" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Second Port of Call</FormLabel>
                <FormControl>
                  <Input className={inputClasses} placeholder="e.g., Port of Antwerp" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <FileText className="size-5 text-primary" />
          <h4 className="font-serif text-xl font-semibold">Supply Requirements</h4>
        </div>
        <div className="space-y-6">
          <FormField control={control} name="supply_type" render={({
          field
        }) => <FormItem className="md:max-w-md">
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Supply Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className={cn("w-full", inputClasses)}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Provisions">Provisions</SelectItem>
                    <SelectItem value="Spare Parts">Spare Parts</SelectItem>
                    <SelectItem value="Cleaning Services">Cleaning Services</SelectItem>
                    <SelectItem value="Technical Services">Technical Services</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription className="font-mono text-[11px]">Select the type of supply you are requesting.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="supply_requirements" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Detailed Requirements</FormLabel>
                <FormControl>
                  <Textarea placeholder="List the items, quantities, and specifications you need (e.g., 10,000 L fuel, 5,000 kg provisions)." className={cn("min-h-[160px] resize-y", inputClasses)} {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[11px]">Provide as much detail as possible to receive an accurate quote.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="additional_details" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Additional Details</FormLabel>
                <FormControl>
                  <Textarea placeholder="Any extra information you wish to provide." className={cn("min-h-[100px] resize-y", inputClasses)} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <UserCheck className="size-5 text-primary" />
          <h4 className="font-serif text-xl font-semibold">Agent Details (Optional)</h4>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <FormField control={control} name="agent_name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Agent Name</FormLabel>
                <FormControl>
                  <Input className={inputClasses} placeholder="" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="agent_email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Agent Email</FormLabel>
                <FormControl>
                  <Input type="email" className={inputClasses} placeholder="" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="agent_phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Agent Phone</FormLabel>
                <FormControl>
                  <Input type="tel" className={inputClasses} placeholder="" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <Anchor className="size-5 text-primary" />
          <h4 className="font-serif text-xl font-semibold">Delivery & Logistics</h4>
        </div>
        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <FormField control={control} name="desired_delivery_date" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-wider">Desired Delivery Date</FormLabel>
                  <FormControl>
                    <Input type="date" className={inputClasses} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="delivery_location" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-wider">Delivery Location</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={cn("w-full", inputClasses)}>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Berth / Terminal">Berth / Terminal</SelectItem>
                      <SelectItem value="Anchorage">Anchorage</SelectItem>
                      <SelectItem value="Offshore">Offshore</SelectItem>
                      <SelectItem value="Shipyard">Shipyard</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="delivery_method" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-wider">Delivery Method</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className={cn("w-full", inputClasses)}>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Ship's Crane">Ship's Crane</SelectItem>
                      <SelectItem value="Port Crane">Port Crane</SelectItem>
                      <SelectItem value="Launch Boat Delivery">Launch Boat Delivery</SelectItem>
                      <SelectItem value="Shore Delivery">Shore Delivery</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="required_certificates" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Required Certificates (if applicable)</FormLabel>
                <FormControl>
                  <Textarea placeholder="e.g., ISO 9001, COA for fuel" className={cn("min-h-[100px] resize-y", inputClasses)} {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[11px]">List any certificates needed for the delivery (e.g., ISO, COA).</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="supporting_documents" render={({
          field: {
            value,
            onChange,
            ...fieldProps
          }
        }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase tracking-wider">Supporting Documents (Optional)</FormLabel>
                <FormControl>
                  <Input type="file" className={cn("cursor-pointer", inputClasses)} onChange={e => onChange(e.target.files?.[0])} {...fieldProps} />
                </FormControl>
                <FormDescription className="font-mono text-[11px]">
                  Upload requisition lists, spare part photos, technical drawings, specification sheets, or any supporting documents related to your request.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="data_consent" render={({
          field
        }) => <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-[2px] border border-border bg-muted/5 p-4">
                <FormControl>
                  <input type="checkbox" className="mt-1 size-4 rounded-[2px] border-border text-primary focus:ring-primary/20 accent-primary" checked={field.value} onChange={e => field.onChange(e.target.checked)} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal leading-relaxed text-muted-foreground">
                    I consent to the processing of my personal data for the purpose of responding to my service inquiry, in accordance with the <Link to="/privacy-policy" className="text-primary underline hover:text-primary/80" newTab>Privacy Policy</Link>.
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>} />
        </div>
      </div>

      <div className="space-y-8">
        <div className="font-mono text-[11px] italic text-muted-foreground" dangerouslySetInnerHTML={{
        __html: `<p style="font-style:italic;">If some information is not yet available, please submit the request with the details currently known. Our team will follow up for any missing information.</p>`
      }} />

        {submitError && <div className="rounded-[2px] border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive font-mono">
            {submitError}
          </div>}

        <div className="flex justify-end border-t border-border pt-8">
          <Button type="submit" size="lg" className="h-14 w-full rounded-[2px] text-lg font-semibold md:w-auto shadow-none" disabled={isSubmitting}>
            {isSubmitting ? <>
                <Loader className="mr-2 size-5 animate-spin" />
                Processing Request...
              </> : "Request Procurement Quote"}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function ProcurementRequirements() {
  return <section id="procurement-requirements" className="bg-background py-24 md:py-40">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center md:mb-20">
            <div className="mb-6 flex justify-center space-x-6">
              <div className="flex size-14 items-center justify-center rounded-[2px] bg-primary/10 text-primary">
                <Ship className="size-7" />
              </div>
              <div className="flex size-14 items-center justify-center rounded-[2px] bg-primary/10 text-primary">
                <Anchor className="size-7" />
              </div>
            </div>
            <h2 className="mb-6 font-serif text-4xl font-bold tracking-tight text-foreground md:text-5xl">
              Procurement Requirements Form
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-muted-foreground">
              Submit your vessel's requirements through our brokerage portal. Our team coordinates with a global network of trusted maritime suppliers to ensure efficient sourcing and precise logistics for your fleet.
            </p>
          </div>

          <div className="rounded-[2px] border border-border bg-card p-8 md:p-12 shadow-none">
            <div className="mb-10 border-b border-border pb-8">
              <h3 className="font-serif text-2xl font-semibold text-card-foreground">Vessel & Contact Information</h3>
              <p className="mt-2 text-muted-foreground">Detailed specifications enable our brokerage team to provide accurate quotations and optimized delivery schedules.</p>
            </div>

            <FormProvider formId="procurement_requirements_form_supply_quote" sectionName="Procurement Requirements" defaultValues={{
            company_name: "",
            vessel_name: "",
            imo_number: "",
            contact_name: "",
            email: "",
            phone: "",
            eta: "",
            etd: "",
            next_port_of_call: "",
            second_port_of_call: "",
            agent_name: "",
            agent_email: "",
            agent_phone: "",
            supply_requirements: "",
            supply_type: "",
            desired_delivery_date: "",
            delivery_location: "",
            delivery_method: "",
            required_certificates: "",
            additional_details: "",
            supporting_documents: "",
            data_consent: false
          }} validationRules={{
            company_name: {
              required: false
            },
            vessel_name: {
              required: false
            },
            imo_number: {
              required: false,
              pattern: "^[0-9]{7}$"
            },
            contact_name: {
              required: false
            },
            email: {
              required: false,
              pattern: "email"
            },
            phone: {
              required: false
            },
            eta: {
              required: false
            },
            etd: {
              required: false
            },
            next_port_of_call: {
              required: false
            },
            supply_requirements: {
              required: false
            },
            data_consent: {
              required: true
            }
          }} submitText="Request Procurement Quote" successMessage="Your procurement request has been successfully submitted. Our brokerage team will now coordinate with our global network of trusted suppliers to obtain quotations tailored to your specific requirements.">
              <ProcurementFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}